import { MultiAutocomplete } from '@common/ui'
import React from 'react'

export type MultiSelectInputProps<T> = {
  available: readonly { [label: string]: T }[]
  selected: readonly T[]
  setSelected: (selected: readonly T[]) => void
  equals?: (value: T, other: T) => boolean
  label?: string
  labelId?: string
  disabled?: boolean
  error?: boolean
  sx?: React.CSSProperties
}

const getItemLabel = <T extends unknown>(item: { [label: string]: T }) => Object.keys(item)[0]
const getItemValue = <T extends unknown>(item: { [label: string]: T }) => item[Object.keys(item)[0]]

const MultiSelectInput = <T extends unknown>({
  available,
  selected,
  setSelected,
  equals = (value, other) => value === other,
  label,
  disabled,
  error,
  sx,
}: MultiSelectInputProps<T>) => {
  const selectedIndices = selected
    .filter((value) => available.some((item) => equals(getItemValue(item), value)))
    .map((value) => available.findIndex((item) => equals(getItemValue(item), value)))

  const data = available.map((item, index) => ({ value: String(index), label: getItemLabel(item) }))

  return (
    <MultiAutocomplete
      data={data}
      value={selectedIndices.map(String)}
      label={label}
      disabled={disabled}
      error={error}
      searchable={false}
      clearable={false}
      style={sx}
      onChange={(values) => {
        const newSelectedIndices = values.map(Number).sort((a, b) => a - b)
        setSelected(newSelectedIndices.map((index) => getItemValue(available[index])))
      }}
    />
  )
}

export default MultiSelectInput
